import './PrintTemplateModalContent.css';

import { useState } from 'react';
import { IonIcon, IonItem, IonList } from '@ionic/react';
import {
  addOutline,
  copyOutline,
  createOutline,
  documentTextOutline,
  trashOutline,
} from 'ionicons/icons';
import ModalBody from '../ControlActionModalContent/components/ModalBody/ModalBody';
import Button from '../../Button/Button';
import { useTranslate } from '../../../hooks/context/useTranslate';
import { useModalKeyboard } from '../../../hooks/shortcuts/useModalKeyboard';
import { ReportTemplateSummary } from '../../../pages/DatasetPrint/types';

export interface PrintTemplateModalContentProps {
  header: string;
  templates: ReportTemplateSummary[];
  onSelect: (templateId: string) => void;
  /** Opens one existing stored template in the designer. */
  onOpenDesigner?: (templateId: string) => Promise<void>;
  /** Creates a fresh user template from the generated system default. */
  onCreateTemplate?: () => Promise<void>;
  /** Creates a fresh user template from one selected stored layout. */
  onCopyTemplate?: (templateId: string) => Promise<void>;
  /** Soft-deactivates one stored template after local confirmation. */
  onDeactivateTemplate?: (templateId: string) => Promise<void>;
  /** The generated default is still usable, but the stored list is incomplete. */
  hasTemplateListError?: boolean;
  onClose: () => void;
}

/** Lists reports, then offers create/edit/deactivate management actions. */
const PrintTemplateModalContent = ({
  header,
  templates,
  onSelect,
  onOpenDesigner,
  onCreateTemplate,
  onCopyTemplate,
  onDeactivateTemplate,
  hasTemplateListError = false,
  onClose,
}: PrintTemplateModalContentProps) => {
  const { t } = useTranslate();
  const [pendingDeactivate, setPendingDeactivate] =
    useState<ReportTemplateSummary | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [copyingTemplateId, setCopyingTemplateId] = useState<string | null>(
    null
  );
  const [createError, setCreateError] = useState<string | null>(null);
  const [openingTemplateId, setOpeningTemplateId] = useState<string | null>(
    null
  );
  const [openError, setOpenError] = useState<string | null>(null);
  const isBusy =
    isCreating ||
    isDeactivating ||
    openingTemplateId !== null ||
    copyingTemplateId !== null;

  useModalKeyboard({
    onEscape: isBusy
      ? undefined
      : pendingDeactivate
      ? () => {
          setPendingDeactivate(null);
          setDeactivateError(null);
        }
      : onClose,
  });

  if (pendingDeactivate) {
    const confirmText = t('print.deactivateConfirm').replace(
      '%s',
      pendingDeactivate.label
    );

    return (
      <ModalBody
        className="print-template-modal"
        header={t('print.deactivateTemplate')}
        text={
          <>
            <p>{confirmText}</p>
            {deactivateError && (
              <p className="print-template-error" role="alert">
                {deactivateError}
              </p>
            )}
          </>
        }
        buttons={
          <>
            <Button
              onClick={() => {
                setPendingDeactivate(null);
                setDeactivateError(null);
              }}
              disabled={isDeactivating}
            >
              {t('cancel')}
            </Button>
            <Button
              color="danger"
              disabled={isDeactivating}
              onClick={async () => {
                if (!onDeactivateTemplate) return;
                setIsDeactivating(true);
                setDeactivateError(null);
                try {
                  await onDeactivateTemplate(pendingDeactivate.templateId);
                  setPendingDeactivate(null);
                } catch {
                  setDeactivateError(t('print.deactivateFailed'));
                } finally {
                  setIsDeactivating(false);
                }
              }}
            >
              {t('print.deactivateTemplate')}
            </Button>
          </>
        }
      />
    );
  }

  return (
    <ModalBody
      className="print-template-modal"
      header={header}
      text={
        <>
          <IonList className="print-template-list">
            {templates.map((template) => (
              <IonItem
                button={true}
                key={template.templateId}
                lines="full"
                disabled={isBusy}
                onClick={() => {
                  if (!isBusy) onSelect(template.templateId);
                }}
              >
                <IonIcon icon={documentTextOutline} slot="start" />
                <span className="print-template-label">
                  {template.isSystemDefault
                    ? t(template.label)
                    : template.label}
                </span>
                {!template.isSystemDefault && onOpenDesigner && (
                  <Button
                    small={true}
                    fill="clear"
                    slot="end"
                    title={t('print.editTemplate')}
                    disabled={isBusy}
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (isBusy) return;
                      setOpeningTemplateId(template.templateId);
                      setOpenError(null);
                      setCreateError(null);
                      try {
                        await onOpenDesigner(template.templateId);
                      } catch {
                        setOpenError(t('print.openFailed'));
                      } finally {
                        setOpeningTemplateId(null);
                      }
                    }}
                  >
                    <IonIcon icon={createOutline} />
                  </Button>
                )}
                {!template.isSystemDefault && onCopyTemplate && (
                  <Button
                    small={true}
                    fill="clear"
                    slot="end"
                    title={t('print.copyTemplate')}
                    disabled={isBusy}
                    onClick={async (event) => {
                      event.stopPropagation();
                      if (isBusy) return;
                      setCopyingTemplateId(template.templateId);
                      setCreateError(null);
                      setOpenError(null);
                      try {
                        await onCopyTemplate(template.templateId);
                      } catch {
                        setCreateError(t('print.createFailed'));
                      } finally {
                        setCopyingTemplateId(null);
                      }
                    }}
                  >
                    <IonIcon icon={copyOutline} />
                  </Button>
                )}
                {!template.isSystemDefault && onDeactivateTemplate && (
                  <Button
                    small={true}
                    fill="clear"
                    color="danger"
                    slot="end"
                    disabled={isBusy}
                    title={t('print.deactivateTemplate')}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (!isBusy) setPendingDeactivate(template);
                    }}
                  >
                    <IonIcon icon={trashOutline} />
                  </Button>
                )}
              </IonItem>
            ))}
            {onCreateTemplate && (
              <IonItem
                button={true}
                disabled={isBusy}
                lines="none"
                className="print-template-designer"
                onClick={async () => {
                  if (isBusy) return;
                  setIsCreating(true);
                  setCreateError(null);
                  setOpenError(null);
                  try {
                    await onCreateTemplate();
                  } catch {
                    setCreateError(t('print.createFailed'));
                  } finally {
                    setIsCreating(false);
                  }
                }}
              >
                <IonIcon icon={addOutline} slot="start" />
                {t(isCreating ? 'print.creatingTemplate' : 'print.newTemplate')}
              </IonItem>
            )}
          </IonList>
          {hasTemplateListError && (
            <p className="print-template-error" role="alert">
              {t('print.templateListFailed')}
            </p>
          )}
          {createError && (
            <p className="print-template-error" role="alert">
              {createError}
            </p>
          )}
          {openError && (
            <p className="print-template-error" role="alert">
              {openError}
            </p>
          )}
        </>
      }
      buttons={
        <Button onClick={onClose} disabled={isBusy}>
          {t('close')}
        </Button>
      }
    />
  );
};

export default PrintTemplateModalContent;
